import React from 'react'
import SignupCircleSVG from 'assets/svgs/signup-circle.svg'
import LoginCircleSVG from 'assets/svgs/login-circle.svg'
import {useLocation} from 'react-use'

import HoverBackground from 'src/components/HoverBackground'
import {Flex, Box, Grid} from 'src/components/Box'
import {constants} from 'src/styles'
import {localPaths} from 'src/urls'
import {useNavDrawerAction} from 'src/context/NavDrawerContext'
import useViewer from 'src/hooks/useViewer'
import {Body} from 'src/components/text'
import Dropdown, {
  DropdownProvider,
  Props as DropdownProps,
} from 'src/components/Dropdown'
import useCoreJourneys from 'src/hooks/useCoreJourneys'
import JourneyIcon from 'src/components/JourneyIcon'
import {DesktopSearchCta, MobileSearchCta} from 'src/components/NavSearch'

import {MenuIcon, Logo} from './icons'
import {Action, ActionLink} from './Action'

const MobileNav = () => {
  const {toggleDrawer} = useNavDrawerAction()

  return (
    <Action
      aria-label="Toggle Drawer"
      onClick={toggleDrawer}
      data-no-click-away="true"
    >
      <Flex height={constants.MOBILE_HEADER_HEIGHT} mx="2" alignItems="center">
        <MenuIcon />
      </Flex>
    </Action>
  )
}

const DesktopLeftNav = () => {
  const coreJourneys = useCoreJourneys()
  return (
    <Grid
      gridAutoFlow="column"
      gridGap="4"
      ml="3"
      height={constants.DESKTOP_HEADER_HEIGHT}
    >
      <DropdownNav
        data-cy="Goal-Nav"
        anchor="left"
        title="Pursue Your Goal"
        hoverIcon="journey"
        dropdownKey="journey"
      >
        {coreJourneys.map((journey) => (
          <ActionLink key={journey.id} to={journey.permalink}>
            <Flex alignItems="center">
              {journey.icon && <JourneyIcon icon={journey.icon} mr="1" />}
              <span>{journey.name}</span>
            </Flex>
          </ActionLink>
        ))}
      </DropdownNav>

      <DropdownNav
        data-cy="Resources-Nav"
        anchor="left"
        title="Resources"
        hoverIcon="resources"
        dropdownKey="resources"
      >
        <ActionLink to={localPaths.sendText}>Text an Advisor</ActionLink>
        <ActionLink to={localPaths.scholarships}>Find Scholarships</ActionLink>
        <ActionLink to={localPaths.collegeAppHelp}>
          Review my College App Essay
        </ActionLink>
        <ActionLink to={localPaths.resumeBuilder}>Build my Resume</ActionLink>
        <ActionLink to={localPaths.resumeHelp}>Review my Resume</ActionLink>
        <ActionLink to={localPaths.interviewCoach}>
          Practice a Job Interview
        </ActionLink>
      </DropdownNav>

      <DropdownNav
        data-cy="Educators-Nav"
        anchor="left"
        title="Educators & Partners"
        hoverIcon="educators"
        dropdownKey="educators"
      >
        <ActionLink to={localPaths.educatorResources}>
          Resources for Educators
        </ActionLink>
        <ActionLink to={localPaths.partner}>Partner with Us</ActionLink>
        <ActionLink to={localPaths.educatorContent}>
          Educator Content Library
        </ActionLink>
        <ActionLink to={localPaths.aboutUs}>About Get Schooled</ActionLink>
      </DropdownNav>
    </Grid>
  )
}

const DropdownNav = ({children, ...props}: DropdownProps) => (
  <Dropdown {...props}>
    <Grid
      css={{
        whiteSpace: 'nowrap',
        alignItems: 'center',
        justifyItems: 'center',
      }}
      gridAutoFlow="column"
      justifyContent="start"
      role="none"
      gridGap="4"
      p="4"
    >
      {children}
    </Grid>
  </Dropdown>
)

const DesktopViewerNav = () => {
  const {viewer, signOut} = useViewer()
  const location = useLocation()
  return (
    <Flex
      as="nav"
      alignItems="center"
      mx="3"
      height={constants.DESKTOP_HEADER_HEIGHT}
    >
      {viewer === 'not-logged-in' ? (
        <Grid gridAutoFlow="column" gridGap="4">
          <HoverBackground src={SignupCircleSVG}>
            <ActionLink
              to={localPaths.signUp}
              state={{returnTo: location.pathname}}
            >
              Sign Up
            </ActionLink>
          </HoverBackground>
          <HoverBackground src={LoginCircleSVG}>
            <ActionLink
              to={localPaths.login}
              state={{returnTo: location.pathname}}
            >
              Log In
            </ActionLink>
          </HoverBackground>
        </Grid>
      ) : (
        viewer && (
          <Dropdown
            data-cy="Logged-In"
            anchor="right"
            title={viewer.student.shortName || 'Me'}
            hoverIcon="user"
            dropdownKey="user-actions"
          >
            <Grid css={{whiteSpace: 'nowrap'}} role="none" gridGap="2" p="4">
              <ActionLink
                role="menuitem"
                to={localPaths.profile}
                state={{returnTo: location.pathname}}
              >
                My Profile
              </ActionLink>
              <Action role="menuitem" onClick={signOut}>
                <Body>Log Out</Body>
              </Action>
            </Grid>
          </Dropdown>
        )
      )}
    </Flex>
  )
}

interface HeaderProps {
  hideNavigationLinks?: boolean
}

const Header = ({hideNavigationLinks = false}: HeaderProps) => {
  const {loggedIn} = useViewer()

  return (
    <DropdownProvider>
      <Grid
        as="header"
        width="100%"
        height={{
          default: constants.MOBILE_HEADER_HEIGHT,
          lg: constants.DESKTOP_HEADER_HEIGHT,
        }}
        css={{alignItems: 'center'}}
        gridTemplateColumns="1fr auto 1fr"
        borderBottom="1px solid"
        borderColor="grey2"
      >
        {!hideNavigationLinks ? (
          <>
            <Box mr="auto" display={{default: 'none', lg: 'block'}}>
              <DesktopLeftNav />
            </Box>
            <Box display={{lg: 'none'}}>
              <MobileSearchCta />
            </Box>
          </>
        ) : null}

        <ActionLink
          to={loggedIn ? localPaths.dashboard : localPaths.home}
          css={{gridColumnStart: 2}}
        >
          <Flex alignItems="center">
            <Logo height={{_: '22px', lg: '28px'}} p="1" />
          </Flex>
        </ActionLink>

        {!hideNavigationLinks ? (
          <Flex ml="auto" alignItems="center">
            <Box display={{default: 'none', lg: 'block'}}>
              <DesktopSearchCta />
            </Box>
            <Box display={{default: 'none', lg: 'block'}}>
              <DesktopViewerNav />
            </Box>
            <Box display={{lg: 'none'}}>
              <MobileNav />
            </Box>
          </Flex>
        ) : null}
      </Grid>
    </DropdownProvider>
  )
}

export default Header
